import clsx from "clsx";
import { Menu, X } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Button, ControlSizeProvider } from "../../../ui/Button.js";
import { rememberPlace, rememberSide, type PartsSide } from "../../wholesale/side.js";

/**
 * The frame every Parts page sits in, on either side of the counter: the
 * top bar with the menu toggle, the side's own sidebar (rendered by the
 * caller, wired to the folded-menu state here), the scrim under the folded
 * menu, and `main` for the page — with desktop control sizes throughout.
 * Below 1280 CSS pixels the sidebar folds into a menu the top bar's button
 * opens; Escape closes it and returns focus to the button.
 *
 * The frame is also where the browser's memory of the side is kept
 * (wholesale spec §4.1): on every location change it records `side` as the
 * last side shown — so the root address and the sign-in redirect reopen
 * it — and the address as that side's place, which the Service | Wholesale
 * switch returns to. Both sides write it, so a day spent on a `/parts`
 * bookmark reopens Service just as a wholesale one reopens Wholesale.
 */

export interface WorkspaceFrameMenu {
  /** `id` the menu toggle names in `aria-controls`. */
  id: string;
  open: boolean;
  /** Called after a destination is chosen, so the folded menu closes. */
  onNavigate: () => void;
}

export interface WorkspaceFrameProps {
  side: PartsSide;
  /** The side's sidebar, given the folded-menu wiring. */
  sidebar: (menu: WorkspaceFrameMenu) => ReactNode;
  /** `workspace--page` for a single scrolling column (form, customers, catalog, report). */
  page?: boolean;
  className?: string;
  /** Class for the top bar and the scrim (the report keeps them off paper with `screen-only`). */
  chromeClassName?: string;
  mainClassName?: string;
  children: ReactNode;
}

export function WorkspaceFrame({ side, sidebar, page = false, className, chromeClassName, mainClassName, children }: WorkspaceFrameProps) {
  const location = useLocation();
  const navId = useId();
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    rememberSide(side);
    rememberPlace(side, `${location.pathname}${location.search}`);
  }, [side, location.pathname, location.search]);

  // Escape closes the collapsed menu and returns focus to its button.
  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMenuOpen(false);
      toggleRef.current?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  return (
    <ControlSizeProvider size="desktop">
      <div
        className={clsx(
          "workspace workspace--parts",
          side === "wholesale" && "workspace--wholesale",
          page && "workspace--page",
          className,
        )}
        data-menu-open={menuOpen ? "true" : "false"}
      >
        <header className={clsx("workspace__topbar", chromeClassName)}>
          <Button
            ref={toggleRef}
            variant="secondary"
            icon={menuOpen ? <X /> : <Menu />}
            className="workspace__nav-toggle"
            aria-expanded={menuOpen}
            aria-controls={navId}
            onClick={() => setMenuOpen((open) => !open)}
          >
            Menu
          </Button>
          <span className="workspace__topbar-brand">Chicagoland Auto Tools</span>
        </header>

        {sidebar({ id: navId, open: menuOpen, onNavigate: () => setMenuOpen(false) })}
        {menuOpen ? (
          <button
            type="button"
            className={clsx("workspace__scrim", chromeClassName)}
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          />
        ) : null}

        <main className={clsx("workspace__main", page && "workspace__main--page", mainClassName)}>{children}</main>
      </div>
    </ControlSizeProvider>
  );
}
